/**
 * OutOfCreditsModal — globally mounted paywall surface.
 *
 * Listens for `window` events:
 *   "cl:out-of-credits"   → opens the modal with the provided detail
 *
 * The detail payload comes from the 402 response of the generate hook:
 *   {
 *     plan: "free" | "pro" | "studio",
 *     error: "credit_exhausted" | "monthly_exhausted",
 *     upgrade?: boolean,
 *     topup?: boolean
 *   }
 *
 * Free users see Upgrade-to-Pro CTA. Paid users see Top-up CTA. Both
 * surfaces also offer a "Wait until reset" affordance with the time.
 *
 * Mounted once in App.tsx at root level so any page that 402s can fire
 * the event without prop-drilling.
 */

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Sparkles, Zap, X, Clock, ArrowRight, Plus, AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import {
  TOP_UP_PACK,
  getTopUpUrl,
  openCheckout,
  isBillingConfigured,
} from "@/lib/billing";
import { useToast } from "@/components/Toast";
import { ROUTES, type PlanTier } from "@/types";

export interface OutOfCreditsDetail {
  plan: PlanTier;
  error: "credit_exhausted" | "monthly_exhausted";
  upgrade?: boolean;
  topup?: boolean;
}

export default function OutOfCreditsModal() {
  const [detail, setDetail] = useState<OutOfCreditsDetail | null>(null);
  const { user } = useAuth();
  const { data: credits, refresh } = useCredits();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Subscribe to the global "out of credits" event.
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<OutOfCreditsDetail>;
      if (ce.detail) setDetail(ce.detail);
    };
    window.addEventListener("cl:out-of-credits", handler as EventListener);
    return () => window.removeEventListener("cl:out-of-credits", handler as EventListener);
  }, []);

  // Esc closes
  useEffect(() => {
    if (!detail) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setDetail(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detail]);

  if (!detail) return null;

  const close = () => setDetail(null);

  const isFree = detail.plan === "free";
  const monthly = detail.error === "monthly_exhausted";
  const resetLabel = credits ? formatReset(
    credits.balance.resetAt || credits.balance.periodEnd,
    credits.balance.resetCadence,
    monthly,
  ) : isFree ? "midnight UTC" : "next billing period";

  const handleUpgrade = () => {
    close();
    navigate(ROUTES.PLANS);
  };

  const handleTopUp = async () => {
    const url = getTopUpUrl();
    if (!isBillingConfigured() || !url) {
      toast.info(
        "Billing not wired yet",
        "Top-ups go live once your LemonSqueezy store is set up.",
      );
      return;
    }
    try {
      await openCheckout(url, {
        userId: user?.id || "",
        email: user?.email,
        onSuccess: () => {
          close();
          void refresh();
          toast.success("Top-up applied", `+${TOP_UP_PACK.credits} generations added to your balance.`);
        },
      });
    } catch (err) {
      toast.error("Couldn't open checkout", err instanceof Error ? err.message : "Try again in a moment");
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="oc-modal-title"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        background: "rgba(20, 14, 22, 0.5)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        display: "grid",
        placeItems: "center",
        padding: 20,
        animation: "oc-fade 200ms var(--ease-smooth)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="animate-dropdown-in"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 520,
          background: "var(--bg-surface-raised)",
          border: "1px solid var(--border-default)",
          borderRadius: 24,
          padding: "36px 32px 28px",
          boxShadow:
            "0 40px 100px -30px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset",
          overflow: "hidden",
        }}
      >
        {/* Atmospheric brand wash behind the icon */}
        <span aria-hidden style={{
          position: "absolute",
          top: -120, left: -80,
          width: 320, height: 320,
          background: "radial-gradient(circle, rgba(255,107,157,0.25), transparent 65%)",
          filter: "blur(40px)",
          pointerEvents: "none",
        }} />
        <span aria-hidden style={{
          position: "absolute",
          top: -100, right: -100,
          width: 320, height: 320,
          background: "radial-gradient(circle, rgba(197,63,255,0.18), transparent 65%)",
          filter: "blur(50px)",
          pointerEvents: "none",
        }} />

        {/* Close */}
        <button
          onClick={close}
          aria-label="Close"
          style={{
            position: "absolute", top: 14, right: 14,
            width: 32, height: 32,
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-default)",
            borderRadius: "50%",
            color: "var(--text-tertiary)",
            cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            zIndex: 1,
          }}
        >
          <X size={14} strokeWidth={1.75} />
        </button>

        {/* Icon */}
        <div style={{
          position: "relative",
          width: 56, height: 56,
          background: "var(--brand-gradient)",
          borderRadius: 18,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          marginBottom: 20,
          boxShadow: "0 20px 40px -16px rgba(255,77,109,0.6)",
        }}>
          <AlertCircle size={26} strokeWidth={1.75} color="#fff" />
        </div>

        {/* Headline */}
        <h2 id="oc-modal-title" style={{
          fontFamily: "var(--font-display)",
          fontSize: 28, fontWeight: 600,
          letterSpacing: "var(--tracking-display-md)",
          lineHeight: 1.15,
          margin: "0 0 8px",
          color: "var(--text-primary)",
        }}>
          {monthly
            ? "You've used your 30 free generations this month"
            : isFree
            ? <>You're out for <span className="brand-text-gradient">today</span></>
            : <>You're out for <span className="brand-text-gradient">this period</span></>}
        </h2>

        {/* Subcopy */}
        <p style={{
          fontSize: "var(--font-size-md)",
          color: "var(--text-secondary)",
          lineHeight: 1.55,
          marginBottom: 24,
        }}>
          {monthly
            ? "Upgrade to Pro, or wait until next month."
            : isFree
            ? "Upgrade to Pro, or wait for the daily reset."
            : `Top up with +${TOP_UP_PACK.credits} for $${TOP_UP_PACK.priceUsd}, or wait for your next cycle.`}
        </p>

        {/* Action cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
          {/* Primary card — Upgrade or Top-up */}
          <button
            onClick={isFree ? handleUpgrade : handleTopUp}
            style={{
              display: "flex", flexDirection: "column", alignItems: "flex-start",
              padding: 16,
              background: "var(--brand-gradient)",
              color: "#fff",
              border: "none",
              borderRadius: 16,
              cursor: "pointer",
              textAlign: "left",
              gap: 6,
              boxShadow: "0 18px 40px -16px rgba(255,77,109,0.55)",
              transition: "transform 160ms var(--ease-smooth), box-shadow 160ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 24px 50px -16px rgba(255,77,109,0.7)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 18px 40px -16px rgba(255,77,109,0.55)";
            }}
          >
            {isFree ? (
              <>
                <Zap size={20} strokeWidth={1.75} />
                <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.85, textTransform: "uppercase", letterSpacing: "var(--tracking-label)", fontFamily: "var(--font-mono)" }}>
                  Recommended
                </span>
                <span style={{ fontSize: 16, fontWeight: 600 }}>Upgrade to Pro</span>
                <span style={{ fontSize: 12, opacity: 0.88, lineHeight: 1.4 }}>
                  500 generations / month · $29
                </span>
                <ArrowRight size={14} strokeWidth={1.75} style={{ marginTop: 4 }} />
              </>
            ) : (
              <>
                <Plus size={20} strokeWidth={1.75} />
                <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.85, textTransform: "uppercase", letterSpacing: "var(--tracking-label)", fontFamily: "var(--font-mono)" }}>
                  Recommended
                </span>
                <span style={{ fontSize: 16, fontWeight: 600 }}>+{TOP_UP_PACK.credits} generations</span>
                <span style={{ fontSize: 12, opacity: 0.88, lineHeight: 1.4 }}>
                  One-time · ${TOP_UP_PACK.priceUsd}
                </span>
                <ArrowRight size={14} strokeWidth={1.75} style={{ marginTop: 4 }} />
              </>
            )}
          </button>

          {/* Secondary card — Wait */}
          <div
            style={{
              display: "flex", flexDirection: "column", alignItems: "flex-start",
              padding: 16,
              background: "var(--bg-surface)",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-default)",
              borderRadius: 16,
              gap: 6,
              textAlign: "left",
            }}
          >
            <Clock size={20} strokeWidth={1.5} style={{ color: "var(--text-tertiary)" }} />
            <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: "uppercase", letterSpacing: "var(--tracking-label)", fontFamily: "var(--font-mono)" }}>
              Free option
            </span>
            <span style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>Wait it out</span>
            <span style={{ fontSize: 12, lineHeight: 1.4 }}>
              Resets {resetLabel}
            </span>
          </div>
        </div>

        {/* Tertiary action — view all plans */}
        {isFree ? (
          <button
            onClick={() => { close(); navigate(ROUTES.PLANS); }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 0",
              background: "transparent", border: "none",
              color: "var(--text-tertiary)",
              fontSize: "var(--font-size-sm)",
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            View all plans
            <ArrowRight size={12} strokeWidth={1.5} />
          </button>
        ) : (
          <button
            onClick={() => { close(); navigate(ROUTES.PLANS); }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 0",
              background: "transparent", border: "none",
              color: "var(--text-tertiary)",
              fontSize: "var(--font-size-sm)",
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            Manage subscription
            <ArrowRight size={12} strokeWidth={1.5} />
          </button>
        )}
      </div>

      <style>{`
        @keyframes oc-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>,
    document.body,
  );
}

/* ── Helpers ─────────────────────────────────────────────────── */

function formatReset(
  iso: string | null,
  cadence: "daily" | "monthly",
  monthly: boolean,
): string {
  if (monthly) {
    // Reset at start of next calendar month.
    const next = new Date();
    next.setUTCMonth(next.getUTCMonth() + 1, 1);
    next.setUTCHours(0, 0, 0, 0);
    return `${next.getUTCDate()} ${next.toLocaleString("en-GB", { month: "long" })}`;
  }
  if (!iso) return cadence === "daily" ? "at midnight UTC" : "next month";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const diffH = Math.round(diffMs / (1000 * 60 * 60));
    if (cadence === "daily") {
      if (diffH <= 1) return "in less than an hour";
      if (diffH < 24) return `in ${diffH} hours`;
      return "tomorrow";
    }
    const month = d.toLocaleString("en-GB", { month: "long" });
    return `on ${d.getUTCDate()} ${month}`;
  } catch {
    return cadence === "daily" ? "at midnight UTC" : "next month";
  }
}

/**
 * Fire the global "out of credits" event from any page that catches
 * a 402 from the generate endpoint. Passes the structured error data
 * through to the modal.
 */
export function showOutOfCredits(detail: OutOfCreditsDetail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("cl:out-of-credits", { detail }));
}
